import axios from 'axios';
import { AnalyticsSessionsResponse, AnalyticsSummary, ProspectDetail, ProspectNote, DashboardStats, ProspectsResponse, PipelineStage, PipelineParticipationResponse, Program, ProgramPricingResponse, Scholarship, Partner, InfoSession, InfoSessionReminderLog, InfoSessionRegistrationAdmin, AdvisorThread, AdvisorMessage, AdvisorThreadSummary, AdvisorUnreadCountAdmin, PresentationStats, PresentationSessionsResponse, PublicProgramDetail, PartnerCampaignResponse, PartnerEligibilitySummary, PartnerOrganizationAdmin } from '../types';
import { env } from '../env';

const API_BASE_URL = env.REACT_APP_API_URL || 'http://127.0.0.1:5001';
const ANALYTICS_DEBUG_IP = env.REACT_APP_ANALYTICS_DEBUG_IP;

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const ANALYTICS_SESSION_KEY = 'analytics_session_id';

export const getAnalyticsSessionId = (): string => {
  if (typeof window === 'undefined') return 'server';
  const existing = localStorage.getItem(ANALYTICS_SESSION_KEY);
  if (existing) return existing;
  const newId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `sess_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
  localStorage.setItem(ANALYTICS_SESSION_KEY, newId);
  return newId;
};

const detectDeviceType = (ua?: string): string | undefined => {
  if (!ua) return undefined;
  const lower = ua.toLowerCase();
  if (lower.includes('ipad') || lower.includes('tablet')) return 'Tablet';
  if (lower.includes('iphone') || lower.includes('ipod') || lower.includes('mobi')) return 'Phone';
  if (lower.includes('android') && !lower.includes('mobile')) return 'Tablet';
  if (lower.includes('android') && lower.includes('mobile')) return 'Phone';
  return 'Computer';
};

type AuthProfile = {
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  title: string | null;
  phone: string | null;
  avatar_key: string | null;
  avatar_url: string | null;
  team: string | null;
  timezone: string | null;
  is_active: boolean;
  notify_email: boolean;
  notify_sms: boolean;
};


// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const impersonatorId = localStorage.getItem('impersonator_id');
  if (impersonatorId) {
    config.headers['X-Impersonator-Id'] = impersonatorId;
  }
  return config;
});

export const prospectService = {
  // Delete a prospect by ID
  async deleteProspect(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/prospects/${id}`);
    return response.data;
  },
  // Get all prospects with filtering
  async getProspects(page = 1, perPage = 20, stage?: string, search?: string): Promise<ProspectsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
    });
    
    if (stage) params.append('stage', stage);
    if (search) params.append('search', search);
    
    const response = await api.get(`/api/admin/prospects?${params}`);
    return response.data;
  },

  // Get a specific prospect with details
  async getProspect(id: number): Promise<ProspectDetail> {
    const response = await api.get(`/api/admin/prospects/${id}`);
    const data = response.data;
    // Backend returns flat object; normalize to { prospect: {...} } if not already wrapped
    if (data && typeof data === 'object' && 'id' in data && !('prospect' in data)) {
      return { prospect: data } as ProspectDetail;
    }
    return data;
  },

  // Create a new prospect (visitor capture)
  async createProspect(data: {
    first_name: string;
    last_name: string;
    email: string;
    phone?: string;
    program_interest?: string;
  }): Promise<{ id: number; user_id?: number; temporary_password?: string; message: string }> {
    const response = await api.post('/api/prospects', data);
    return response.data;
  },

  // Advance prospect to next stage
  async advanceProspect(id: number, notes?: string): Promise<{ message: string; new_stage: string }> {
    const response = await api.post(`/api/admin/prospects/${id}/advance`, { notes });
    return response.data;
  },

  // Update prospect with detailed information (questionnaire)
  async updateProspectDetails(id: number, data: any): Promise<{ message: string }> {
    const response = await api.put(`/api/admin/prospects/${id}`, data);
    return response.data;
  },

  // Advance prospect to specific stage (not just next)
  async advanceProspectToStage(id: number, stage: string, notes?: string): Promise<{ message: string; new_stage: string }> {
    const response = await api.post(`/api/admin/prospects/${id}/stage`, { stage, notes });
    return response.data;
  },

  // Add note to prospect
  async addNote(id: number, content: string): Promise<{ id: number; message: string }> {
    const response = await api.post(`/api/admin/prospects/${id}/notes`, { content });
    return response.data;
  },

  // Get prospect notes (alias for compatibility)
  async getProspectNotes(id: number): Promise<ProspectNote[]> {
    const response = await api.get(`/api/admin/prospects/${id}/notes`);
    return response.data;
  },

  // Add prospect note (alias for compatibility)
  async addProspectNote(id: number, content: string): Promise<{ id: number; message: string }> {
    return this.addNote(id, content);
  },

  // Get scholarships for prospects
  async getScholarships(): Promise<Scholarship[]> {
  const response = await api.get('/api/scholarships');
  const data = response.data;
  // Backend returns a raw array (marshal_list_with) not wrapped; support both
  if (Array.isArray(data)) return data;
  return data.scholarships || [];
  },

  // Create a new scholarship (admin only)
  async createScholarship(data: Omit<Scholarship, 'id' | 'created_at' | 'updated_at'>): Promise<Scholarship> {
  const response = await api.post('/api/admin/scholarships', data);
  const body = response.data;
  return body.scholarship || body; // backend returns the scholarship object directly
  },

  // Update an existing scholarship (admin only)
  async updateScholarship(id: number, data: Partial<Omit<Scholarship, 'id' | 'created_at' | 'updated_at'>>): Promise<Scholarship> {
  const response = await api.put(`/api/admin/scholarships/${id}`, data);
  const body = response.data;
  return body.scholarship || body;
  },

  // Delete a scholarship (admin only)
  async deleteScholarship(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/scholarships/${id}`);
    return response.data;
  },
};

export const dashboardService = {
  // Get dashboard statistics
  async getStats(): Promise<DashboardStats> {
    const response = await api.get('/api/admin/dashboard/stats');
    return response.data;
  },
};

export const presentationService = {
  async startSession(data: { presentation_id: string; referrer?: string; viewport?: string }): Promise<{ session_id: string }> {
    const response = await api.post('/api/presentation/sessions', data);
    return response.data;
  },
  async logEvent(data: {
    session_id: string;
    event_type: string;
    watch_seconds?: number;
    payload?: Record<string, unknown>;
  }): Promise<{ message: string }> {
    const response = await api.post('/api/presentation/events', data);
    return response.data;
  },
  async getStats(presentationId: string): Promise<PresentationStats> {
    const response = await api.get(`/api/presentation/stats?presentation_id=${encodeURIComponent(presentationId)}`);
    return response.data;
  },
  async getSessions(presentationId: string, page = 1, perPage = 50): Promise<PresentationSessionsResponse> {
    const params = new URLSearchParams({
      presentation_id: presentationId,
      page: page.toString(),
      per_page: perPage.toString(),
    });
    const response = await api.get(`/api/presentation/sessions/list?${params.toString()}`);
    return response.data;
  },
};

export const analyticsService = {
  async trackEvent(data: {
    event_name: string;
    page_path?: string;
    page_title?: string;
    page_type?: string;
    program_id?: number;
    content_id?: string;
    step?: string;
    referrer?: string;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    payload?: Record<string, unknown>;
    screen_width?: number;
    screen_height?: number;
  }): Promise<void> {
    const impersonatorId = typeof window !== 'undefined'
      ? localStorage.getItem('impersonator_id')
      : null;
    const userAgent = typeof window !== 'undefined' ? navigator.userAgent : undefined;
    const screenWidth = data.screen_width ?? (typeof window !== 'undefined' ? window.screen?.width : undefined);
    const screenHeight = data.screen_height ?? (typeof window !== 'undefined' ? window.screen?.height : undefined);
    const payload = {
      session_id: getAnalyticsSessionId(),
      event_name: data.event_name,
      impersonator_id: impersonatorId ? Number(impersonatorId) : undefined,
      page_path: data.page_path,
      page_title: data.page_title,
      page_type: data.page_type,
      program_id: data.program_id,
      content_id: data.content_id,
      step: data.step,
      referrer: data.referrer,
      utm_source: data.utm_source,
      utm_medium: data.utm_medium,
      utm_campaign: data.utm_campaign,
      utm_term: data.utm_term,
      utm_content: data.utm_content,
      payload: data.payload,
      screen_width: screenWidth,
      screen_height: screenHeight,
      device_type: detectDeviceType(userAgent),
      user_agent: userAgent,
    };
    const headers = ANALYTICS_DEBUG_IP ? { 'X-Forwarded-For': ANALYTICS_DEBUG_IP } : undefined;
    await api.post('/api/analytics/events', payload, headers ? { headers } : undefined);
  },
  async getSummary(days = 30): Promise<AnalyticsSummary> {
    const params = new URLSearchParams({
      days: days.toString(),
    });
    const response = await api.get(`/api/admin/analytics/summary?${params.toString()}`);
    return response.data;
  },
  async getSessions(days = 30, page = 1, perPage = 50): Promise<AnalyticsSessionsResponse> {
    const params = new URLSearchParams({
      days: days.toString(),
      page: page.toString(),
      per_page: perPage.toString(),
    });
    const response = await api.get(`/api/admin/analytics/sessions/list?${params.toString()}`);
    return response.data;
  },
};

export const pipelineService = {
  // Get pipeline stages
  async getStages(): Promise<{ stages: PipelineStage[] }> {
    const response = await api.get('/api/pipeline/stages');
    return response.data;
  },
  async getParticipation(userId: number, pipelineId?: string): Promise<PipelineParticipationResponse> {
    const params = pipelineId ? `?pipeline_id=${encodeURIComponent(pipelineId)}` : '';
    const response = await api.get(`/api/pipeline/participation/${userId}${params}`);
    return response.data;
  },
};

export const authService = {
  // Login
  async login(username: string, password: string): Promise<{ message: string; token: string; user: any }> {
    const response = await api.post('/api/auth/login', { username, password });
    return response.data;
  },

  // Register
  async register(data: {
    username: string;
    email: string;
    password: string;
    first_name: string;
    last_name: string;
    phone?: string;
    program_interest?: string;
  }): Promise<{ message: string; user_id: number; prospect_id: number }> {
    const response = await api.post('/api/auth/register', data);
    return response.data;
  },
  async setPassword(data: { new_password: string; confirm_password: string; current_password?: string }): Promise<{ message: string }> {
    const response = await api.post('/api/auth/set-password', data);
    return response.data;
  },
  async requestPasswordReset(data: { email?: string; phone?: string; channel?: 'email' | 'sms' }): Promise<{ message: string }> {
    const response = await api.post('/api/auth/request-password-reset', data);
    return response.data;
  },
  async requestAdminPasswordReset(data: { email: string; message?: string; expires_minutes?: number }): Promise<{ message: string }> {
    const response = await api.post('/api/admin/auth/request-password-reset', data);
    return response.data;
  },
  async importLegacyUser(data: { user: any; prospect: any; keep_password_hash?: boolean }): Promise<{ message: string; user_id: number; lead_id: number }> {
    const response = await api.post('/api/admin/auth/import-legacy-user', data);
    return response.data;
  },
  async resetPassword(token: string, newPassword: string, confirmPassword: string): Promise<{ message: string }> {
    const response = await api.post('/api/auth/reset-password', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    return response.data;
  },
  async impersonate(userId: number): Promise<{ message: string; token: string; user: any; impersonator_id?: number }> {
    const response = await api.post('/api/auth/impersonate', { user_id: userId });
    return response.data;
  },

  // Logout
  async logout(): Promise<void> {
    await api.post('/api/auth/logout');
  },

  // Check if user is authenticated
  async checkAuth(): Promise<{ user: any } | null> {
    try {
      const response = await api.get('/api/auth/me');
      return response.data;
    } catch {
      return null;
    }
  },
  async getProfile(): Promise<AuthProfile> {
    const response = await api.get('/api/auth/profile');
    return response.data;
  },
  async updateProfile(data: Partial<AuthProfile>): Promise<AuthProfile> {
    const response = await api.put('/api/auth/profile', data);
    return response.data;
  },
};

export const userService = {
  // Get user's own application
  async getMyApplication(): Promise<{ application: any }> {
    const response = await api.get('/api/student-pathway');
    const data = response.data;
    // Backend returns a flat application object (marshal_with) not wrapped; normalize
    if (data && typeof data === 'object' && !('application' in data) && 'id' in data) {
      return { application: data };
    }
    return data;
  },

  // Update user's own application
  async updateMyApplication(data: any): Promise<{ message: string }> {
  const response = await api.put('/api/student-pathway', data);
    return response.data;
  },

  async getStudentPhotoUploadUrl(payload: { filename: string; content_type: string }): Promise<{ upload_url: string; key: string }> {
    const response = await api.post('/api/student-pathway/photo/upload-url', payload);
    return response.data;
  },

  async createApplicationFeeIntent(): Promise<{ payment_id: number; client_secret: string | null; amount: number; currency: string }> {
    const response = await api.post('/api/payments/student/application-fee/intent');
    return response.data;
  },

  async confirmStudentPayment(payload: { payment_id?: number; payment_intent_id?: string; consented?: boolean; terms_version?: string }): Promise<{ status: string }> {
    const response = await api.post('/api/payments/student/confirm', payload);
    return response.data;
  },

  async getPaymentSummary(): Promise<{ application_fee_amount: number; application_fee_currency: string; application_fee_paid: boolean; payments_total: number }> {
    const response = await api.get('/api/payments/student/summary');
    return response.data;
  },

  async resendApplicationFeeReceipt(): Promise<{ message: string }> {
    const response = await api.post('/api/payments/student/application-fee/receipt');
    return response.data;
  },

  // Advance user's own application
  async advanceMyApplication(notes?: string): Promise<{ message: string; new_stage: string }> {
  const response = await api.post('/api/student-pathway/advance', { notes });
    return response.data;
  },
  async setStepCompletion(payload: { key: string; completed: boolean }): Promise<{ message: string; key: string; completed: boolean }> {
    const response = await api.post('/api/student-pathway/step-completion', payload);
    return response.data;
  },
};

export const professorService = {
  async getOnboarding(): Promise<{ profile: any; stages: any[] }> {
    const response = await api.get('/api/professor/onboarding');
    return response.data;
  },
  async advanceOnboarding(): Promise<{ message: string; profile: any }> {
    const response = await api.post('/api/professor/onboarding/advance', {});
    return response.data;
  }
};

export const programService = {
  // Get all active programs (backend returns raw array)
  async getPrograms(): Promise<Program[]> {
    const response = await api.get('/api/programs');
    const data = response.data;
    return Array.isArray(data) ? data : (data.programs || []);
  },

  async getPublicProgramDetails(): Promise<PublicProgramDetail[]> {
    const response = await api.get('/api/programs/public-details');
    const data = response.data;
    return Array.isArray(data) ? data : (data.programs || []);
  },

  // Admin: get all programs including inactive
  async getAllPrograms(): Promise<Program[]> {
    const response = await api.get('/api/admin/programs');
    const data = response.data;
    return Array.isArray(data) ? data : (data.programs || []);
  },

  // Create a new program (admin only)
  async createProgram(data: Partial<Program>): Promise<Program> {
    const response = await api.post('/api/admin/programs', data);
    return response.data;
  },

  // Update an existing program (admin only)
  async updateProgram(id: number, data: Partial<Program>): Promise<Program> {
    const response = await api.put(`/api/admin/programs/${id}`, data);
    return response.data;
  },

  async deleteProgram(id: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/programs/${id}`);
    return response.data;
  },

  async getProgramPricing(programId: number): Promise<ProgramPricingResponse> {
    const response = await api.get(`/api/programs/${programId}/pricing`);
    return response.data;
  },

  async updateProgramPricing(programId: number, data: ProgramPricingResponse): Promise<ProgramPricingResponse> {
    const response = await api.put(`/api/admin/programs/${programId}/pricing`, data);
    return response.data;
  },

  async createProgramCohort(programId: number, data: {
    name: string;
    starts_on?: string | null;
    ends_on?: string | null;
    application_deadline?: string | null;
    capacity?: number | null;
    seats_available?: number | null;
    selling_price?: number | null;
    estimated_selling_price?: number | null;
    description?: string | null;
    classes_offered?: string[] | null;
    included_items?: string[] | null;
    meeting_times?: string | null;
    status?: string | null;
  }): Promise<any> {
    const response = await api.post(`/api/admin/programs/${programId}/cohorts`, data);
    return response.data;
  },

  async getProgramCohorts(programId: number): Promise<any[]> {
    const response = await api.get(`/api/admin/programs/${programId}/cohorts`);
    return response.data || [];
  },

  async updateProgramCohort(programId: number, cohortId: number, data: {
    name?: string;
    starts_on?: string | null;
    ends_on?: string | null;
    application_deadline?: string | null;
    capacity?: number | null;
    seats_available?: number | null;
    selling_price?: number | null;
    estimated_selling_price?: number | null;
    description?: string | null;
    classes_offered?: string[] | null;
    included_items?: string[] | null;
    meeting_times?: string | null;
    status?: string | null;
    is_active?: boolean;
  }): Promise<any> {
    const response = await api.put(`/api/admin/programs/${programId}/cohorts/${cohortId}`, data);
    return response.data;
  },

  async deleteProgramCohort(programId: number, cohortId: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/programs/${programId}/cohorts/${cohortId}`);
    return response.data;
  },
};

export const infoSessionService = {
  async listUpcoming(): Promise<InfoSession[]> {
    const response = await api.get('/api/info-sessions');
    return response.data || [];
  },
  async listAdmin(): Promise<InfoSession[]> {
    const response = await api.get('/api/admin/info-sessions');
    return response.data || [];
  },
  async create(data: {
    title: string;
    description?: string;
    starts_at: string;
    duration_minutes: number;
    timezone: string;
    join_url: string;
    capacity?: number;
    is_active?: boolean;
    reminders_enabled?: boolean;
    reminder_offsets?: number[];
    reminder_channels?: string[];
    host_name?: string;
    host_email?: string;
  }): Promise<{ message: string; id: number }> {
    const response = await api.post('/api/admin/info-sessions', data);
    return response.data;
  },
  async update(sessionId: number, data: {
    title?: string;
    description?: string;
    starts_at?: string;
    duration_minutes?: number;
    timezone?: string;
    join_url?: string;
    capacity?: number;
    is_active?: boolean;
    reminders_enabled?: boolean;
    reminder_offsets?: number[];
    reminder_channels?: string[];
    host_name?: string;
    host_email?: string;
  }): Promise<{ message: string }> {
    const response = await api.put(`/api/admin/info-sessions/${sessionId}`, data);
    return response.data;
  },
  async delete(sessionId: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/info-sessions/${sessionId}`);
    return response.data;
  },
  async sendTestReminder(sessionId: number, channel?: 'email' | 'sms'): Promise<{ message: string; errors?: string[] }> {
    const response = await api.post(`/api/admin/info-sessions/${sessionId}/reminders/test`, channel ? { channel } : {});
    return response.data;
  },
  async listReminderLogs(limit = 100): Promise<InfoSessionReminderLog[]> {
    const response = await api.get(`/api/admin/info-sessions/reminders?limit=${limit}`);
    return response.data || [];
  },
  async listRegistrationsAdmin(sessionId: number): Promise<InfoSessionRegistrationAdmin[]> {
    const response = await api.get(`/api/admin/info-sessions/${sessionId}/registrations`);
    return response.data || [];
  },
  async register(sessionId: number): Promise<{ message: string; registration_id?: number }> {
    const response = await api.post(`/api/info-sessions/${sessionId}/register`);
    return response.data;
  },
  async cancel(sessionId: number): Promise<{ message: string }> {
    const response = await api.post(`/api/info-sessions/${sessionId}/cancel`);
    return response.data;
  },
};

export const testimonialService = {
  async validateRequest(token: string): Promise<{ message: string; program_id?: number; email?: string }> {
    const response = await api.get(`/api/testimonials/request/${token}`);
    return response.data;
  },
  async getPhotoUploadUrl(payload: { token: string; filename: string; content_type: string }): Promise<{ upload_url: string; key: string }> {
    const response = await api.post('/api/testimonials/photo/upload-url', payload);
    return response.data;
  },
  async getApproved(programId?: number): Promise<Array<{
    id: number;
    name?: string;
    role_title?: string;
    quote?: string;
    story?: string;
    countries?: string[];
    photo_url?: string;
    program_id?: number | null;
  }>> {
    const query = programId ? `?program_id=${programId}` : '';
    const response = await api.get(`/api/testimonials/approved${query}`);
    return response.data || [];
  },
  async submit(payload: {
    token: string;
    name?: string;
    role_title?: string;
    quote: string;
    story?: string;
    countries?: string[];
    current_location?: string;
    consent_publish: boolean;
    consent_photo?: boolean;
    photo_url?: string;
  }): Promise<{ id: number; message: string; status: string }> {
    const response = await api.post('/api/testimonials/submit', payload);
    return response.data;
  },
};

export const testimonialAdminService = {
  async sendRequests(payload: {
    recipients: Array<{ lead_id?: number; email?: string; phone?: string }>;
    program_id?: number;
    channel?: 'email' | 'sms';
    expires_days?: number;
    subject?: string;
    message?: string;
  }): Promise<any[]> {
    const response = await api.post('/api/admin/testimonials/requests', payload);
    return response.data || [];
  },
  async resendRequest(requestId: number): Promise<any> {
    const response = await api.post(`/api/admin/testimonials/requests/${requestId}/resend`);
    return response.data;
  },
  async listSubmissions(): Promise<any[]> {
    const response = await api.get('/api/admin/testimonials/submissions');
    return response.data || [];
  },
  async approveSubmission(submissionId: number): Promise<{ message: string; status: string }> {
    const response = await api.post(`/api/admin/testimonials/submissions/${submissionId}/approve`);
    return response.data;
  },
  async deleteSubmission(submissionId: number): Promise<{ message: string }> {
    const response = await api.delete(`/api/admin/testimonials/submissions/${submissionId}`);
    return response.data;
  },
};

export const advisorService = {
  async getThread(): Promise<AdvisorThread> {
    const response = await api.get('/api/advisor/thread');
    const data = response.data || {};
    return { messages: [], ...data };
  },
  async getUnreadCount(): Promise<{ unread_count: number }> {
    const response = await api.get('/api/advisor/unread-count');
    const data = response.data || {};
    return { unread_count: data.unread_count ?? data.count ?? 0 };
  },
  async sendMessage(body: string): Promise<AdvisorMessage> {
    const response = await api.post('/api/advisor/thread/messages', { body });
    return response.data;
  },
  async markThreadRead(): Promise<{ message: string; unread_count: number }> {
    const response = await api.post('/api/advisor/thread/read');
    return response.data || { message: 'Marked read', unread_count: 0 };
  },
  async listThreadsAdmin(): Promise<AdvisorThreadSummary[]> {
    const response = await api.get('/api/admin/advisor/threads');
    const data = response.data;
    if (Array.isArray(data)) return data;
    return data?.threads || [];
  },
  async getThreadAdmin(prospectId: number): Promise<AdvisorThread> {
    const response = await api.get(`/api/admin/advisor/threads/${prospectId}`);
    const data = response.data || {};
    return { messages: [], ...data };
  },
  async markThreadReadAdmin(prospectId: number): Promise<{ message: string; unread_count_admin: number }> {
    const response = await api.post(`/api/admin/advisor/threads/${prospectId}/read`);
    return response.data || { message: 'Marked read', unread_count_admin: 0 };
  },
  async getUnreadCountAdmin(): Promise<AdvisorUnreadCountAdmin> {
    const response = await api.get('/api/admin/advisor/unread-count');
    return response.data || { unread_count: 0, unread_threads: [] };
  },
  async replyAdmin(prospectId: number, body: string): Promise<AdvisorMessage> {
    const response = await api.post(`/api/admin/advisor/threads/${prospectId}/messages`, { body });
    return response.data;
  },
};

export const partnerService = {
  async getPartners(partnerType?: string): Promise<Partner[]> {
    const query = partnerType ? `?type=${encodeURIComponent(partnerType)}` : '';
    const response = await api.get(`/api/admin/partners${query}`);
    return response.data;
  },

  async getPartnerTypes(): Promise<string[]> {
    const response = await api.get('/api/admin/partners/types');
    return response.data.types || [];
  },

  async createPartner(data: Partial<Partner>): Promise<Partner> {
    const response = await api.post('/api/admin/partners', data);
    return response.data;
  },

  async updatePartner(id: number, data: Partial<Partner>): Promise<Partner> {
    const response = await api.put(`/api/admin/partners/${id}`, data);
    return response.data;
  },

  async deletePartner(id: number): Promise<{ status: string }> {
    const response = await api.delete(`/api/admin/partners/${id}`);
    return response.data;
  },
};

export const partnerDiscountService = {
  async listOrgs(): Promise<PartnerOrganizationAdmin[]> {
    const response = await api.get('/api/admin/partner-discounts/orgs');
    return response.data.orgs || [];
  },

  async createOrg(payload: {
    name: string;
    slug: string;
    verification_type: string;
    email_domain?: string | null;
    validation_url?: string | null;
    validation_token?: string | null;
    is_active?: boolean;
  }): Promise<{ org: PartnerOrganizationAdmin }> {
    const response = await api.post('/api/admin/partner-discounts/orgs', payload);
    return response.data;
  },

  async createCampaign(orgId: number, payload: {
    discount_type?: 'amount' | 'percent';
    discount_value?: number;
    discount_amount?: number;
    currency: string;
    description?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    is_active?: boolean;
  }): Promise<any> {
    const response = await api.post(`/api/admin/partner-discounts/orgs/${orgId}/campaigns`, payload);
    return response.data;
  },

  async updateOrg(orgId: number, payload: {
    name?: string;
    slug?: string;
    verification_type?: string;
    email_domain?: string | null;
    validation_url?: string | null;
    validation_token?: string | null;
    is_active?: boolean;
  }): Promise<{ org: PartnerOrganizationAdmin }> {
    const response = await api.put(`/api/admin/partner-discounts/orgs/${orgId}`, payload);
    return response.data;
  },

  async updateCampaign(orgId: number, campaignId: number, payload: {
    discount_type?: 'amount' | 'percent';
    discount_value?: number;
    discount_amount?: number;
    currency?: string;
    description?: string | null;
    starts_at?: string | null;
    ends_at?: string | null;
    is_active?: boolean;
  }): Promise<any> {
    const response = await api.put(`/api/admin/partner-discounts/orgs/${orgId}/campaigns/${campaignId}`, payload);
    return response.data;
  },

  async uploadRoster(orgId: number, entries: Array<{ email?: string; member_id?: string; last_name?: string }>): Promise<{ created: number; updated: number }> {
    const response = await api.post(`/api/admin/partner-discounts/orgs/${orgId}/roster`, { entries });
    return response.data;
  },

  async getCampaign(slug: string): Promise<PartnerCampaignResponse> {
    const response = await api.get(`/api/partner-discounts/${encodeURIComponent(slug)}`);
    return response.data;
  },

  async sendEmailOtp(slug: string, email: string): Promise<{ message: string }> {
    const response = await api.post(`/api/partner-discounts/${encodeURIComponent(slug)}/verify-email`, { email });
    return response.data;
  },

  async verifyEmail(slug: string, email: string, code: string): Promise<{ message: string; grant?: any }> {
    const response = await api.post(`/api/partner-discounts/${encodeURIComponent(slug)}/verify`, { email, code });
    return response.data;
  },

  async verifyMember(slug: string, member_id: string, last_name: string, email?: string): Promise<{ message: string; grant?: any }> {
    const response = await api.post(`/api/partner-discounts/${encodeURIComponent(slug)}/verify`, { member_id, last_name, email });
    return response.data;
  },

  async getEligibility(): Promise<PartnerEligibilitySummary> {
    const response = await api.get('/api/partner-discounts/eligibility');
    return response.data;
  },
};

export default api;
